import { useSuspenseQuery } from '@tanstack/react-query';
import {
    getAuth,
    type IdTokenResult,
    onAuthStateChanged,
    onIdTokenChanged,
    type User,
} from 'firebase/auth';
import * as React from 'react';

import type { UserCustomClaims } from '@gitbook/app-database';
import { assert } from '@gitbook/util-ts';

import { handleAuthListenerError } from './operations';
import type { FirebaseAuthProviderId } from './types';

/**
 * Firebase listeners are triggered at initialization.
 * Wrap a callback to skip the first call.
 */
function skipFirstCall(callback: () => void): () => void {
    let firstCall = true;

    return () => {
        if (firstCall) {
            firstCall = false;
        } else {
            callback();
        }
    };
}

/**
 * Subscription to access the firebase user.
 */
const currentUserSubscription = {
    getCurrentValue: () => getAuth().currentUser,
    subscribe: (callback) => {
        const auth = getAuth();
        const unsubscribeTokenChanged = onIdTokenChanged(
            auth,
            skipFirstCall(callback),
            handleAuthListenerError
        );
        const unsubscribeStateChanged = onAuthStateChanged(
            auth,
            skipFirstCall(callback),
            handleAuthListenerError
        );

        return () => {
            unsubscribeTokenChanged();
            unsubscribeStateChanged();
        };
    },
};

const CurrentUserContext = React.createContext<User | null>(null);

export function CurrentUserProvider(props: { children: React.ReactNode }) {
    const user = React.useSyncExternalStore(
        currentUserSubscription.subscribe,
        currentUserSubscription.getCurrentValue
    );
    return <CurrentUserContext.Provider value={user}>{props.children}</CurrentUserContext.Provider>;
}

/**
 * Get the current firebase user.
 */
export function useCurrentUser(): User | null {
    return React.useContext(CurrentUserContext);
}

/**
 * Get the current firebase user or throw if not authenticated.
 */
export function useLoggedInUser(): User {
    const currentUser = useCurrentUser();
    assert(currentUser, 'This component requires an authenticated user.');
    return currentUser;
}

type TokenResult = IdTokenResult & {
    claims: UserCustomClaims;
};

/**
 * Return the id token result of to the current user.
 */
export function useUserIdTokenResult(): TokenResult | null {
    const auth = getAuth();
    const { data, refetch } = useSuspenseQuery({
        queryKey: ['userIdTokenResult'],
        staleTime: 0,
        queryFn: async () => {
            return auth.currentUser?.getIdTokenResult() ?? null;
        },
    });
    React.useEffect(
        () =>
            auth.onIdTokenChanged(
                skipFirstCall(() => {
                    refetch();
                }),
                handleAuthListenerError
            ),
        [auth.onIdTokenChanged, refetch]
    );
    return data;
}

/**
 * Return the custom claims linked to the current user.
 */
export function useUserCustomClaims(): UserCustomClaims {
    const userIdTokenResult = useUserIdTokenResult();
    if (userIdTokenResult === null) {
        const claims: UserCustomClaims = {};
        return claims;
    }
    return userIdTokenResult.claims;
}

/**
 * Listen to the current user authentication providers.
 */
export function useUserProviders(): FirebaseAuthProviderId[] {
    const currentUser = useLoggedInUser();

    return React.useMemo(
        () => currentUser.providerData.map((info) => info.providerId as FirebaseAuthProviderId),
        [currentUser]
    );
}
